import beautify from 'js-beautify';

import type {
  IconDefinition,
  IconPrefix,
  IconName,
} from '@fortawesome/fontawesome-svg-core';

export const formatTitle = (title: string): string => {
  const rawTitle = (title ?? '').trim();
  if (!rawTitle) {
    return '';
  }

  const withoutPrefix = rawTitle.replace(/^MENU_/, '');
  const hasMenuPrefix = rawTitle !== withoutPrefix;
  const looksLikeKey = hasMenuPrefix || /^[A-Z0-9_&/ -]+$/.test(withoutPrefix);
  const base = looksLikeKey
    ? withoutPrefix.replace(/_/g, ' ').toLowerCase()
    : withoutPrefix;

  return base.replace(/[A-Za-z0-9]+/g, (word) => {
    if (!looksLikeKey && /[A-Z]/.test(word.slice(1))) {
      return word;
    }
    const lower = word.toLowerCase();
    return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
  });
};

export const fetchSvg = async (iconName: string): Promise<string | null> => {
  try {
    const response = await fetch(
      `/assets/svgs/${iconName}.svg?t=${Date.now()}`
    );
    if (!response.ok) {
      console.error(`Failed to load ${iconName}.svg`);
      return null;
    }
    return await response.text();
  } catch (error) {
    console.error('Error loading SVG:', error);
    return null;
  }
};

export const makeSvgIcon = (
  iconName: string,
  svgText: string
): IconDefinition | null => {
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
  const svgElement = svgDoc.querySelector('svg');
  const paths = Array.from(svgDoc.querySelectorAll('path'))
    .map((p) => p.getAttribute('d'))
    .filter((d): d is string => Boolean(d));

  if (!svgElement || paths.length === 0) {
    return null;
  }

  const viewBox = svgElement.getAttribute('viewBox') ?? '0 0 512 512';
  const [, , widthStr, heightStr] = viewBox.split(' ');
  const width = Number.parseInt(widthStr, 10) || 512;
  const height = Number.parseInt(heightStr, 10) || 512;

  return {
    prefix: 'fac' as IconPrefix,
    iconName: iconName as IconName,
    icon: [width, height, [], '', paths.join(' ')],
  };
};

export function formatTimestamp(timestamp: string | number): string {
  const date = new Date(Number(timestamp));
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export const formatTimestampToDateTime = (
  timestamp: number | string
): string => {
  if (!timestamp) {
    return '';
  }

  const date =
    typeof timestamp === 'string' && Number.isNaN(Number(timestamp))
      ? new Date(timestamp)
      : new Date(Number(timestamp));

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${day}/${month}/${year} ${String(hours).padStart(
    2,
    '0'
  )}:${minutes} ${ampm}`;
};

export const formatTimestampToDate = (timestamp: number | string): string => {
  if (!timestamp) {
    return '';
  }

  const date =
    typeof timestamp === 'string' && Number.isNaN(Number(timestamp))
      ? new Date(timestamp)
      : new Date(Number(timestamp));

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const formatTimestampToISODate = (
  timestamp: number | string
): string => {
  if (!timestamp) {
    return '';
  }

  const date =
    typeof timestamp === 'string' && Number.isNaN(Number(timestamp))
      ? new Date(timestamp)
      : new Date(Number(timestamp));

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

/**
 * Converts a time string (HH:MM or HH:MM:SS) to 12-hour format with AM/PM
 * @param timeString - Time in 24-hour format (e.g., "17:00" or "17:00:00")
 * @returns Time in 12-hour format (e.g., "5:00 PM")
 */
export const formatTimeTo12Hour = (
  timeString: string | undefined | null
): string => {
  if (!timeString) return '-';

  const [hourStr, minuteStr] = timeString.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = minuteStr || '00';

  if (isNaN(hour)) return '-';

  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;

  return `${hour12}:${minute} ${period}`;
};

function recursivelyFormatObjects(obj: unknown): unknown {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    for (const key of Object.keys(obj as Record<string, unknown>)) {
      const value = (obj as Record<string, unknown>)[key];
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          (obj as Record<string, unknown>)[key] =
            recursivelyFormatObjects(parsed);
        } catch {
          // keep as string
        }
      } else if (
        typeof (obj as Record<string, unknown>)[key] === 'object' &&
        (obj as Record<string, unknown>)[key] !== null
      ) {
        (obj as Record<string, unknown>)[key] = recursivelyFormatObjects(
          (obj as Record<string, unknown>)[key]
        );
      }
    }
  }
  return obj;
}

export function formatCodeOrJson(input: string): string {
  if (!input) {
    return '';
  }
  try {
    const parsed = JSON.parse(input);
    const formatted = JSON.stringify(recursivelyFormatObjects(parsed), null, 2);
    return beautify.js(formatted, {
      indent_size: 2,
      space_in_empty_paren: true,
      e4x: true,
      preserve_newlines: true,
      max_preserve_newlines: 2,
      end_with_newline: true,
      brace_style: 'collapse',
      wrap_line_length: 80,
    });
  } catch {
    return beautify.js(input, {
      indent_size: 2,
      space_in_empty_paren: true,
      e4x: true,
      preserve_newlines: true,
      max_preserve_newlines: 2,
      end_with_newline: true,
      brace_style: 'collapse',
      wrap_line_length: 80,
    });
  }
}

export function getInitials(str: string): string {
  const words = str.trim().split(/\s+/);
  const initials = words.map((word) => word.charAt(0).toUpperCase());
  return initials.join('');
}

export const getValidRoute = (route?: string | null) => {
  if (!route) return null;
  const trimmed = route.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('//')) {
    return `/${trimmed.replace(/^\/+/, '')}`;
  }

  if (trimmed.startsWith('/')) {
    const normalized = trimmed.replace(/\/{2,}/g, '/');
    return normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized;
  }

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const url = new URL(trimmed);
      if (url.hash?.startsWith('#/')) {
        const hashPath = url.hash.slice(1);
        return hashPath.length > 1 ? hashPath.replace(/\/+$/, '') : hashPath;
      }
      const path = `${url.pathname}${url.search}${url.hash}`.trim();
      const normalized = path.startsWith('/') ? path : `/${path}`;
      return normalized.length > 1
        ? normalized.replace(/\/+$/, '')
        : normalized;
    }
  } catch {
    // ignore invalid URL parsing
  }

  const fallback = `/${trimmed}`;
  return fallback.length > 1 ? fallback.replace(/\/+$/, '') : fallback;
};
